/* ════════════════════════════════════════════════════════════
   JiraniApp Business Dashboard — Mock Data + Pricing Model
   No backend yet. This file is the single source of truth for
   every section component during the UI-design pass.

   BILLING MODEL (confirmed):
   One flat monthly subscription per tier. No per-booking charges
   on reserved-Pro jobs, no team add-on fee. The subscription
   includes a fixed number of reserved Pro slots, a monthly
   booking allowance, and a number of dashboard team-member seats.
   Businesses can always book from the public marketplace at
   standard rates — those bookings are on-demand, billed
   separately, and never count against the subscription limit.
   ════════════════════════════════════════════════════════════ */

/* ── Platform subscription tiers (business-facing) ──
   maxPros          → reserved-Pro slots included
   bookingsPerMonth → subscription booking allowance (null = unlimited / negotiated)
   maxTeamMembers   → dashboard user seats
   proPool          → "standard" | "priority" (Business Priority pool access)
   prioritySupport  → boolean
   apiAccess        → boolean                                                   */
export const PLAN_LIMITS = {
  starter: {
    key: "starter",
    label: "Starter",
    price: 24000,
    maxPros: 3,
    bookingsPerMonth: 10,
    maxTeamMembers: 3,
    proPool: "standard",
    prioritySupport: false,
    apiAccess: false,
    blurb: "For a single site getting started with a small reserved team.",
  },
  business: {
    key: "business",
    label: "Business",
    price: 35000,
    maxPros: 6,
    bookingsPerMonth: 15,
    maxTeamMembers: 6,
    proPool: "standard",
    prioritySupport: false,
    apiAccess: false,
    blurb: "For growing operations managing multiple sites and a dedicated team.",
  },
  enterprise: {
    key: "enterprise",
    label: "Enterprise",
    price: 55000,
    maxPros: 10,              // ← reduced from 15
    bookingsPerMonth: 20,
    maxTeamMembers: 12,
    proPool: "priority",
    prioritySupport: true,
    apiAccess: false,
    blurb: "For multi-site organisations that need priority Pros and support.",
  },
  custom: {
    key: "custom",
    label: "Custom",
    price: null,
    maxPros: null,
    bookingsPerMonth: null,
    maxTeamMembers: null,
    proPool: "priority",
    prioritySupport: true,
    apiAccess: true,
    blurb: "Negotiated limits, API access, and dedicated support for your scale.",
  },
};

/* Ordered list for plan-comparison tables / upgrade flows */
export const PLAN_ORDER = ["starter", "business", "enterprise"];

/* ── The logged-in business account ── */
export const MOCK_BUSINESS_ACCOUNT = {
  id: "biz_001",
  name: "Cedar Heights Property Management",
  type: "business",
  plan: "business",
  bookingsUsedThisMonth: 11,
  phone: "+254 712 445 902",
  email: "ops@cedarheights.co.ke",
  memberSince: "March 2026",
  logoInitials: "CH",
  city: "Nairobi",
  hasPersonalAccount: true,   // drives account switcher visibility
  paymentMethod: "mpesa",     // "mpesa" | "bank"
  bankDetails: {
    bankName: "",
    branch: "",
    accountNumber: "",
    accountName: "",
    swiftCode: "",
  },
};

/* ── The linked personal account (for account switcher) ── */
export const MOCK_PERSONAL_ACCOUNT = {
  id: "personal_001",
  name: "Amani Wekesa",
  type: "personal",
  initials: "AW",
  email: "amani@gmail.com",
  phone: "0712345678",
  hasBusinessAccount: true,   // drives account switcher visibility
};

/* ── Reserved Pros (My Team) — drawn from the subscription's maxPros allowance ── */
export const MOCK_TEAM_PROS = [
  {
    id: "pro_101",
    name: "Daniel Mwangi",
    role: "Plumbing & Maintenance",
    initials: "DM",
    colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" },
    status: "available",
    reservedSince: "Apr 2026",
    rating: 4.9,
    jobsThisMonth: 14,
    commitment: "Standard (3-4 days/week)",
  },
  {
    id: "pro_102",
    name: "Faith Njeri",
    role: "Cleaning & Housekeeping",
    initials: "FN",
    colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    status: "on_job",
    reservedSince: "Apr 2026",
    rating: 5.0,
    jobsThisMonth: 22,
    commitment: "Full exclusive",
  },
  {
    id: "pro_103",
    name: "Brian Otieno",
    role: "Electrical",
    initials: "BO",
    colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
    status: "off_duty",
    reservedSince: "May 2026",
    rating: 4.8,
    jobsThisMonth: 9,
    commitment: "Light (1-2 days/week)",
  },
  {
    id: "pro_104",
    name: "Grace Wambui",
    role: "Gardening & Grounds",
    initials: "GW",
    colorSet: { bg: "#fffbeb", text: "#b45309", border: "#fde68a" },
    status: "available",
    reservedSince: "May 2026",
    rating: 4.7,
    jobsThisMonth: 11,
    commitment: "Standard (3-4 days/week)",
  },
];

/* ── Curated list of Pros available to reserve (standard pool) ── */
export const MOCK_AVAILABLE_PROS = [
  { id: "av_201", name: "Samuel Kiptoo",  role: "Electrical",             initials: "SK", rating: 4.9, jobs: 312, city: "Nairobi", colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" } },
  { id: "av_202", name: "Mercy Achieng",  role: "Cleaning & Housekeeping", initials: "MA", rating: 4.8, jobs: 264, city: "Nairobi", colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" } },
  { id: "av_203", name: "Peter Kamau",    role: "Plumbing & Maintenance",  initials: "PK", rating: 5.0, jobs: 198, city: "Nairobi", colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" } },
  { id: "av_204", name: "Lucy Wairimu",   role: "Gardening & Grounds",    initials: "LW", rating: 4.7, jobs: 145, city: "Nairobi", colorSet: { bg: "#fffbeb", text: "#b45309", border: "#fde68a" } },
  { id: "av_205", name: "Kevin Omondi",   role: "Security & Patrol",      initials: "KO", rating: 4.9, jobs: 287, city: "Nairobi", colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" } },
  { id: "av_206", name: "Esther Njoki",   role: "Cleaning & Housekeeping", initials: "EN", rating: 4.8, jobs: 176, city: "Nairobi", colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" } },
];

/* ── Business Priority pool — curated, highest-rated Pros opted into the
   business programme. Visible only to Enterprise / Custom accounts. ── */
export const MOCK_PRIORITY_PROS = [
  { id: "pr_301", name: "Joseph Mutua",   role: "Electrical",             initials: "JM", rating: 5.0, jobs: 540, city: "Nairobi", colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" } },
  { id: "pr_302", name: "Catherine Njau", role: "Cleaning & Housekeeping", initials: "CN", rating: 4.9, jobs: 489, city: "Nairobi", colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" } },
  { id: "pr_303", name: "Abdi Hassan",    role: "Plumbing & Maintenance",  initials: "AH", rating: 4.9, jobs: 412, city: "Mombasa", colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" } },
];

/* ── Subscription bookings — drawn from the monthly allowance, no per-job charge ── */
export const MOCK_BOOKINGS = [
  { id: "bk_1", service: "Plumbing repair, Block C",       pro: "Daniel Mwangi", location: "Cedar Heights, Block C",      date: "Jun 22, 2026", time: "9:00 AM",   status: "confirmed",   amount: 3500, recurring: false, billingType: "subscription" },
  { id: "bk_2", service: "Weekly deep clean",              pro: "Faith Njeri",   location: "Cedar Heights, Lobby",         date: "Jun 21, 2026", time: "7:30 AM",   status: "in_progress", amount: 4200, recurring: true,  billingType: "subscription" },
  { id: "bk_3", service: "Garden maintenance",             pro: "Grace Wambui",  location: "Cedar Heights, Grounds",       date: "Jun 24, 2026", time: "8:00 AM",   status: "scheduled",   amount: 2800, recurring: true,  billingType: "subscription" },
  { id: "bk_4", service: "Electrical inspection, Block A", pro: "Brian Otieno",  location: "Cedar Heights, Block A",       date: "Jun 18, 2026", time: "2:00 PM",   status: "completed",   amount: 5000, recurring: false, billingType: "subscription" },
  { id: "bk_5", service: "Window cleaning, all blocks",    pro: "Mercy Achieng", location: "Cedar Heights, All blocks",    date: "Jun 15, 2026", time: "10:00 AM",  status: "completed",   amount: 6500, recurring: false, billingType: "subscription" },
  { id: "bk_6", service: "Plumbing emergency callout",     pro: "Daniel Mwangi", location: "Cedar Heights, Block B",       date: "Jun 12, 2026", time: "11:45 PM",  status: "completed",   amount: 4800, recurring: false, billingType: "subscription" },
  { id: "bk_7", service: "Pest control, common areas",     pro: "Unassigned",    location: "Cedar Heights, Common areas",  date: "Jun 10, 2026", time: "9:00 AM",   status: "cancelled",   amount: null, recurring: false, billingType: "subscription" },
];

/* ── On-demand marketplace bookings — outside the subscription, billed
   at the Pro's listed rate, itemised separately on the invoice ── */
export const MOCK_ON_DEMAND_BOOKINGS = [
  { id: "od_1", service: "One-off carpet shampoo, Block A", pro: "Esther Njoki",  location: "Cedar Heights, Block A", date: "Jun 19, 2026", time: "1:00 PM",  status: "completed", amount: 3800, recurring: false, billingType: "on_demand" },
  { id: "od_2", service: "Locksmith, Unit 14B",             pro: "Samuel Kiptoo", location: "Cedar Heights, Block B", date: "Jun 14, 2026", time: "4:30 PM",  status: "completed", amount: 2200, recurring: false, billingType: "on_demand" },
  { id: "od_3", service: "TV mounting, Management Office",  pro: "Kevin Omondi",  location: "Management Office",      date: "Jun 8, 2026",  time: "11:00 AM", status: "completed", amount: 1800, recurring: false, billingType: "on_demand" },
];

/* ── Locations ── */
export const MOCK_LOCATIONS = [
  { id: "loc_1", label: "Cedar Heights, Block A", address: "Riverside Drive, Nairobi", type: "Residential", units: 24, activeJobs: 1 },
  { id: "loc_2", label: "Cedar Heights, Block B", address: "Riverside Drive, Nairobi", type: "Residential", units: 24, activeJobs: 0 },
  { id: "loc_3", label: "Cedar Heights, Block C", address: "Riverside Drive, Nairobi", type: "Residential", units: 18, activeJobs: 1 },
  { id: "loc_4", label: "Management Office",       address: "Riverside Drive, Nairobi", type: "Office",      units: 1,  activeJobs: 0 },
  { id: "loc_5", label: "Cedar Heights, Grounds",  address: "Riverside Drive, Nairobi", type: "Shared",      units: 1,  activeJobs: 1 },
];

/* ── Invoices — simplified structure:
   one line for the flat subscription fee, one line for itemised
   on-demand marketplace charges that month, one total. ── */
export const MOCK_INVOICES = [
  {
    id: "inv_2026_06", period: "June 2026",  status: "upcoming", dueDate: "Jul 1, 2026",  paidOn: null,
    subscriptionFee: 35000, onDemandCharges: 7800,  total: 42800, lineItems: 4,
  },
  {
    id: "inv_2026_05", period: "May 2026",   status: "paid",     dueDate: "Jun 1, 2026",  paidOn: "Jun 1, 2026",
    subscriptionFee: 35000, onDemandCharges: 5400,  total: 40400, lineItems: 3,
  },
  {
    id: "inv_2026_04", period: "April 2026", status: "paid",     dueDate: "May 1, 2026",  paidOn: "May 2, 2026",
    subscriptionFee: 35000, onDemandCharges: 0,     total: 35000, lineItems: 1,
  },
  {
    id: "inv_2026_03", period: "March 2026", status: "paid",     dueDate: "Apr 1, 2026",  paidOn: "Mar 31, 2026",
    subscriptionFee: 24000, onDemandCharges: 3200,  total: 27200, lineItems: 2,
  },
];

/* ── Team members (dashboard users, not Pros) ── */
export const MOCK_MEMBERS = [
  { id: "mem_1", name: "Amani Wekesa",     email: "amani@cedarheights.co.ke",    role: "Owner",           initials: "AW", colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" }, lastActive: "Today"      },
  { id: "mem_2", name: "Joyce Mutiso",     email: "joyce@cedarheights.co.ke",    role: "Admin",           initials: "JM", colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" }, lastActive: "Today"      },
  { id: "mem_3", name: "Hassan Ali",       email: "hassan@cedarheights.co.ke",   role: "Booking Manager", initials: "HA", colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" }, lastActive: "Yesterday"  },
  { id: "mem_4", name: "Caroline Wanjiru", email: "caroline@cedarheights.co.ke", role: "Viewer",          initials: "CW", colorSet: { bg: "#fffbeb", text: "#b45309", border: "#fde68a" }, lastActive: "3 days ago" },
];

/* ── Reports / Overview chart data ── */
export const MOCK_MONTHLY_SPEND = [
  { month: "Jan", value: 14200 },
  { month: "Feb", value: 15800 },
  { month: "Mar", value: 27200 },
  { month: "Apr", value: 35000 },
  { month: "May", value: 40400 },
  { month: "Jun", value: 42800 },
];

export const MOCK_JOBS_BY_CATEGORY = [
  { label: "Plumbing",    value: 28, color: "#1e9254" },
  { label: "Cleaning",    value: 34, color: "#2563eb" },
  { label: "Electrical",  value: 14, color: "#7c3aed" },
  { label: "Gardening",   value: 18, color: "#b45309" },
  { label: "Other",       value: 6,  color: "#8a93a8" },
];

export const MOCK_OVERVIEW_STATS = {
  activeBookings:  3,
  reservedPros:    4,
  monthToDateSpend: 18400,
  avgResponseTime: "11 min",
  completionRate:  98,
  openDisputes:    0,
};

/* ── Notifications ── */
export const MOCK_NOTIFICATIONS = [
  {
    id: "n1",
    icon: "CheckCircle",
    iconColor: "#1b7a47",
    title: "Job marked complete",
    body: "Daniel Mwangi finished the plumbing repair at Block C.",
    time: "12 min ago",
    read: false,
    linkTo: "/bookings/bk_001",
  },
  {
    id: "n2",
    icon: "FileText",
    iconColor: "#2563eb",
    title: "Invoice ready",
    body: "Your June invoice of KES 42,800 is ready to view.",
    time: "2 hours ago",
    read: false,
    linkTo: "/business/account?tab=billing",
  },
  {
    id: "n3",
    icon: "UserPlus",
    iconColor: "#7c3aed",
    title: "Reserved Pro assigned",
    body: "Grace Wambui has joined your team for gardening.",
    time: "Yesterday",
    read: true,
    linkTo: "/business/account?tab=team",
  },
  {
    id: "n4",
    icon: "AlertTriangle",
    iconColor: "#b45309",
    title: "Booking limit approaching",
    body: "You've used 11 of 15 bookings on your Business plan this month.",
    time: "2 days ago",
    read: true,
    linkTo: "/business/account?tab=bookings",
  },
];

/* ── Messages ── */
export const MOCK_MESSAGES = [
  {
    id: "m1",
    conversationId: "conv_001",
    name: "Daniel Mwangi",
    initials: "DM",
    colorSet: { bg: "#eef8f3", text: "#155c36", border: "#d6f0e3" },
    preview: "I'll be at Block C by 9am tomorrow, just confirming access.",
    time: "10 min ago",
    unread: true,
  },
  {
    id: "m2",
    conversationId: "conv_002",
    name: "Faith Njeri",
    initials: "FN",
    colorSet: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    preview: "Lobby clean is done, sending photos now.",
    time: "1 hour ago",
    unread: true,
  },
  {
    id: "m3",
    conversationId: "conv_003",
    name: "Joyce Mutiso",
    initials: "JM",
    colorSet: { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
    preview: "Can you approve the Block A invoice when you get a chance?",
    time: "Yesterday",
    unread: false,
  },
];